import { firestore } from "./firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import {
  Appointment,
  Commitment,
  FixedCommitment,
  Professional,
} from "../@data";
import { generateAvailableTimeSlots } from "../utils";
import { getCustomLunchsByDate } from "./custom-lunchs";
import { WorkingHours } from "./working-hours";

const COLLECTION = "appointments";
const COMMITMENT_COLLECTION = "commitments";
const FIXED_COMMITMENT_COLLECTION = "fixedCommitments";

export const getAppointmentsByDate = async (
  date: string
): Promise<Appointment[]> => {
  // Converte a data para timestamp de início e fim do dia
  const startOfDay = new Date(date + "T00:00:00").getTime();
  const endOfDay = new Date(date + "T23:59:59").getTime();

  const q = query(
    collection(firestore, COLLECTION),
    where("timestamp", ">=", startOfDay),
    where("timestamp", "<=", endOfDay)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Appointment));
};

export const createAppointment = async (
  data: Omit<Appointment, "id" | "createdAt" | "updatedAt">
): Promise<string> => {
  const now = Date.now();
  const appointmentData = {
    ...data,
    createdAt: now,
    updatedAt: now,
    origin: "CLIENT",
  };
  const docRef = await addDoc(
    collection(firestore, COLLECTION),
    appointmentData
  );
  return docRef.id;
};

export const updateAppointment = async (
  id: string,
  data: Partial<Omit<Appointment, "id" | "createdAt" | "updatedAt">>
) => {
  const ref = doc(firestore, COLLECTION, id);
  await updateDoc(ref, { ...data, updatedAt: Date.now() });
};

export const deleteAppointment = async (id: string) => {
  const ref = doc(firestore, COLLECTION, id);
  await deleteDoc(ref);
};

export const getAppointmentsByClientAndDateRange = async (
  clientId: string,
  startDate: number,
  endDate: number
): Promise<Appointment[]> => {
  const q = query(
    collection(firestore, COLLECTION),
    where("clientId", "==", clientId),
    where("timestamp", ">=", startDate),
    where("timestamp", "<=", endDate)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Appointment));
};

export const getAppointmentsByDateRange = async (
  startDate: number,
  endDate: number
): Promise<Appointment[]> => {
  const q = query(
    collection(firestore, COLLECTION),
    where("timestamp", ">=", startDate),
    where("timestamp", "<=", endDate)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Appointment));
};

export const getAppointmentsByProfessionalAndDate = async (
  professionalId: string,
  date: string
): Promise<Appointment[]> => {
  // Converte a data para timestamp de início e fim do dia
  const startOfDay = new Date(date + "T00:00:00").getTime();
  const endOfDay = new Date(date + "T23:59:59").getTime();

  const q = query(
    collection(firestore, COLLECTION),
    where("professionalId", "==", professionalId),
    where("timestamp", ">=", startOfDay),
    where("timestamp", "<=", endOfDay)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Appointment));
};

// Funções para compromissos
export const getAllCommitments = async (): Promise<Commitment[]> => {
  const snapshot = await getDocs(collection(firestore, COMMITMENT_COLLECTION));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Commitment));
};

export const getCommitmentsByDate = async (
  date: string
): Promise<Commitment[]> => {
  // Converte a data para timestamp de início e fim do dia
  const startOfDay = new Date(date + "T00:00:00").getTime();
  const endOfDay = new Date(date + "T23:59:59").getTime();

  const q = query(
    collection(firestore, COMMITMENT_COLLECTION),
    where("timestamp", ">=", startOfDay),
    where("timestamp", "<=", endOfDay)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Commitment));
};

export const getCommitmentsByProfessionalAndDate = async (
  professionalId: string,
  date: string
): Promise<Commitment[]> => {
  // Converte a data para timestamp de início e fim do dia
  const startOfDay = new Date(date + "T00:00:00").getTime();
  const endOfDay = new Date(date + "T23:59:59").getTime();

  const q = query(
    collection(firestore, COMMITMENT_COLLECTION),
    where("professionalId", "==", professionalId),
    where("timestamp", ">=", startOfDay),
    where("timestamp", "<=", endOfDay)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Commitment));
};

export const createCommitment = async (
  data: Omit<Commitment, "id" | "createdAt" | "updatedAt">
) => {
  const now = Date.now();
  const commitmentData = {
    ...data,
    createdAt: now,
    updatedAt: now,
  };
  await addDoc(collection(firestore, COMMITMENT_COLLECTION), commitmentData);
};

export const updateCommitment = async (
  id: string,
  data: Partial<Omit<Commitment, "id" | "createdAt" | "updatedAt">>
) => {
  const ref = doc(firestore, COMMITMENT_COLLECTION, id);
  await updateDoc(ref, { ...data, updatedAt: Date.now() });
};

export const deleteCommitment = async (id: string) => {
  const ref = doc(firestore, COMMITMENT_COLLECTION, id);
  await deleteDoc(ref);
};

// Funções para FixedCommitments
export const getAllFixedCommitments = async (): Promise<FixedCommitment[]> => {
  const snapshot = await getDocs(
    collection(firestore, FIXED_COMMITMENT_COLLECTION)
  );
  return snapshot.docs.map(
    (d) => ({ id: d.id, ...d.data() } as FixedCommitment)
  );
};

export const getFixedCommitmentsByProfessional = async (
  professionalId: string
): Promise<FixedCommitment[]> => {
  const q = query(
    collection(firestore, FIXED_COMMITMENT_COLLECTION),
    where("professionalId", "==", professionalId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (d) => ({ id: d.id, ...d.data() } as FixedCommitment)
  );
};

export const isTimeSlotAvailable = async (
  professional: Professional,
  date: string,
  time: string,
  workingHours: WorkingHours,
  serviceDuration: number
): Promise<boolean> => {
  const [appointments, commitments, fixedCommitments, customLunchs] =
    await Promise.all([
      getAppointmentsByDate(date),
      getCommitmentsByDate(date),
      getAllFixedCommitments(),
      getCustomLunchsByDate(date),
    ]);

  const slots = generateAvailableTimeSlots(
    professional,
    date,
    workingHours,
    appointments,
    commitments,
    fixedCommitments,
    customLunchs,
    serviceDuration
  );

  return slots.find((slot) => slot.time === time)?.available ?? false;
};
